<script setup lang="ts">
/**
 * 端口圆点。
 *
 * 纯展示组件，不含连线逻辑 —— 连线由上层（图引擎适配层）处理。
 * 上层只需要根据当前拖线状态传入 dimmed / linkTarget 即可得到
 * Blender 那种「拖线时高亮兼容端口、其他端口变暗」的效果。
 *
 * ## ⚠️ 颜色走 `data-socket-type` 属性，**不走内联样式**
 *
 * 改动前这里是 `:style="{ '--np-socket-color': socketColor(type) }"` ——
 * 把 JS 里算出来的十六进制值塞进内联变量。后果是**主题够不到端口色**：
 * 值在模块加载时就固化了，CSS 无论怎么写都改不了（所以浅色主题下
 * 端口颜色一直没变过）。
 *
 * 现在只输出一个属性，颜色由 `theme.css` 里
 * `.np-socket[data-socket-type='number'] { --np-socket-color: … }` 决定。
 * 附带的好处是主题写一段任意 CSS 就能直接针对某一种端口调形状。
 */
import { computed } from 'vue'
import type { SocketType } from '../types/socket'

/**
 * ⚠️ 端口的样子（类型 / 精确标记 / 句柄 id）**统一在这个组件里落到 DOM 上**：
 *
 * ```text
 *   data-socket-type="component"   ← 配色与验证都读它
 *   data-strict="true"             ← 精确连接（只收同一种类型）
 *   data-socket-id="items.0"       ← 句柄 id
 * ```
 *
 * 以前这三样由**每个调用方**各自 `v-bind`（三个行组件 + 图那一层），
 * 于是"漏了一个"的表现是"某个端口少个属性、验证脚本读不到" ——
 * 而行组件还有 200 行的棘轮盯着，加两行就得动别处。
 * 现在调用方只给 `def`（或显式的 `type`），属性由这里一次算出来。
 */
const props = withDefaults(
  defineProps<{
    /**
     * 端口的完整定义。给了它就不必再传 `type` / `strict` ——
     * 行组件与图那层都传它，省掉一处处 `v-bind`。
     */
    def?: { id: string; type: SocketType; strict?: boolean }
    type?: SocketType
    side?: 'input' | 'output'
    /** 已有连线 → 实心 */
    connected?: boolean
    /** 拖线中且本端口不兼容 → 变暗 */
    dimmed?: boolean
    /** 拖线中且本端口是合法落点 → 高亮 */
    linkTarget?: boolean
    /** 本次拖拽的起点端口 → 常亮标记 */
    linkOrigin?: boolean
    /**
     * **精确连接**（只接受完全相同的类型）—— 见 `SocketDef.strict`。
     *
     * ⚠️ 摆在 DOM 上（`data-strict`）不只是为了样式：验证脚本要能断言
     * "这个端口是精确的" —— 否则"拖出去高亮了不该连的端点"这种问题
     * 只能靠人眼看。
     */
    strict?: boolean
  }>(),
  {
    def: undefined,
    type: undefined,
    side: 'input',
    connected: false,
    dimmed: false,
    linkTarget: false,
    linkOrigin: false,
    strict: false,
  },
)

/** 类型：`def` 优先（调用方通常只给 def） */
const socketType = computed<SocketType>(() => props.def?.type ?? props.type ?? 'any')
/** 句柄 id：只从 `def` 来（显式传 type 的调用方没有 id 可给） */
const socketId = computed(() => props.def?.id ?? '')
/** 精确连接：`def.strict` 或显式的 `strict`，任一为真即可 */
const isStrict = computed(() => props.def?.strict === true || props.strict === true)
</script>

<template>
  <span
    class="np-socket"
    :class="[
      `np-socket--${side}`,
      {
        'is-connected': connected,
        'is-dimmed': dimmed,
        'is-link-target': linkTarget,
        'is-link-origin': linkOrigin,
      },
    ]"
    :data-socket-type="socketType"
    :data-strict="isStrict ? 'true' : undefined"
    :data-socket-id="socketId || undefined"
  />
</template>
